import type { KkmHandshakeReady, KkmServerAdapter } from '@/shared/api';

import { captureUnexpectedError } from '@/shared/lib';
import {
  type RegisterShiftKktReopenInput,
  type ShiftFieldsFragment,
  ShiftStatus,
} from '@shared/api/graphql/gql/graphql';

import { awaitOpenSessionProof } from './fiscal-operation-session';
import { executeFiscalShiftAction, proofFromShiftCommand } from './fiscal-shift-actions';

type ShiftKktReopenAdapter = Pick<KkmServerAdapter, 'closeShift' | 'handshake' | 'openShift'>;

export interface ShiftKktReopenServices {
  register: (input: RegisterShiftKktReopenInput) => Promise<ShiftFieldsFragment>;
}

export interface ReopenExternallyClosedKktRequest {
  cashierName: string;
  cashierVatin?: string | null;
  /** Касса из привязки рабочего места: смена не открывается на чужой ККТ. */
  expectedKktDeviceId?: string | null;
  shift: ShiftFieldsFragment;
}

export class ShiftKktReopenController {
  public constructor(
    private readonly adapter: ShiftKktReopenAdapter,
    private readonly services: ShiftKktReopenServices,
  ) {}

  public detect(shift: ShiftFieldsFragment | null, handshake: KkmHandshakeReady): boolean {
    if (shift?.status !== ShiftStatus.Open || handshake.kkt.sessionState !== 1) {
      return false;
    }

    reportKktShiftDesync({
      locationId: shift.location_id,
      shiftId: shift.id,
    });

    return true;
  }

  public async reopen(request: ReopenExternallyClosedKktRequest): Promise<ShiftFieldsFragment> {
    if (request.shift.status !== ShiftStatus.Open) {
      throw new Error('В Морсе нет открытой смены. Обновите страницу.');
    }

    const action = await executeFiscalShiftAction(this.adapter, 'open', request.cashierName, request.cashierVatin, {
      expectedKktDeviceId: request.expectedKktDeviceId,
    });
    const proof =
      proofFromShiftCommand(action.handshake, action.openShift) ??
      // Ответ команды не назвал номер смены — читаем состояние с ретраями: `SessionState`
      // догоняет напечатанный документ не мгновенно, а однократное чтение ловит и транзиентный сбой.
      (await awaitOpenSessionProof(this.adapter, action.handshake.kktDevice.idDevice));

    if (proof === null) {
      throw new Error('Касса не подтвердила новую смену. Не повторяйте команду и свяжитесь с управляющим.');
    }

    return this.services.register({ ...proof, shiftId: request.shift.id });
  }
}

export function reportKktShiftDesync(context: { locationId?: string; operationId?: string; shiftId?: string }): void {
  console.warn('[cash-shift.kkt-closed-externally]', context);
  captureUnexpectedError('KKT shift closed outside Mors', context);
}

/** Обратное направление рассинхрона: смену открыли мимо Морса — с пульта ККТ или сторонним ПО. */
export function reportKktShiftOrphan(context: { locationId?: string; sessionNumber: number | null }): void {
  console.warn('[cash-shift.kkt-open-without-mors-shift]', context);
  captureUnexpectedError('KKT shift open without a Mors shift', {
    locationId: context.locationId,
    sessionNumber: String(context.sessionNumber),
  });
}
